import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Target, TrendingUp, TrendingDown, MessageSquare, Mic, Zap, Ticket, ShieldCheck, Gavel, Save, Settings } from "lucide-react";
import { toast } from "sonner";

const METRIC_META = {
  joins: { label: "Server Joins", color: "var(--accent)", icon: TrendingUp },
  leaves: { label: "Server Leaves", color: "#ff3b5c", icon: TrendingDown },
  messages: { label: "Messages", color: "#00e5ff", icon: MessageSquare },
  voice_hours: { label: "Voice Hours", color: "#ffc700", icon: Mic },
  server_bumps: { label: "Server Bumps", color: "#ff9f1c", icon: Zap },
  admin_tickets: { label: "Admin Tickets", color: "var(--accent)", icon: Ticket },
  age_verification_tickets: { label: "Age Verification", color: "#00e5ff", icon: ShieldCheck },
  moderation_tickets: { label: "Moderation Tickets", color: "#ff9f1c", icon: Gavel },
  tickets_combined: { label: "Tickets Handled (combined)", color: "var(--accent)", icon: Ticket },
};

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtMonth(m) {
  const idx = Number(m.split("-")[1]) - 1;
  return MONTH_LABELS[idx];
}

function pct(a, b) {
  if (!b) return 0;
  return Math.min(999, Math.round((a / b) * 100));
}

export default function Metrics() {
  const { user } = useAuth();
  const { brand } = useBranding();
  const [data, setData] = useState(null);
  const [showEntry, setShowEntry] = useState(false);
  const [showGoals, setShowGoals] = useState(false);
  const isOwner = user.role === "admin";
  const canEdit = user.role === "admin" || user.role === "manager";

  const load = () => api.get("/metrics/dashboard").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-slate-500 text-sm">Loading metrics…</div>;

  const view = data.view;
  const kpiKeys = view === "owner" ? data.metric_keys : [...(data.ticket_keys || []), "tickets_combined", "server_bumps"];
  const historyCombined = view === "owner" ? null : data.history;

  const historyForChart = data.history.map((row) => ({ ...row, monthLabel: fmtMonth(row.month) }));

  return (
    <div className="space-y-6" data-testid="metrics-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: brand.accent_color }}>
            {view === "owner" ? "Owner Dashboard" : view === "admin" ? "Admin Dashboard" : "Mod Dashboard"}
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Server Metrics</h1>
          <p className="text-slate-400 mt-2 text-sm">
            Tracking {data.year} · current month {fmtMonth(data.current_month)} · goals & yearly attainment
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowEntry(true)}
              data-testid="open-entry"
              className="px-4 py-2.5 text-black rounded-lg font-bold flex items-center gap-2 hover:opacity-90"
              style={{ background: brand.accent_color }}
            >
              <Save size={16} /> Log Metric
            </button>
            {isOwner && (
              <button
                onClick={() => setShowGoals(true)}
                data-testid="open-goals"
                className="px-4 py-2.5 border rounded-lg font-bold flex items-center gap-2 text-slate-200 hover:text-white transition-colors"
                style={{ borderColor: "#2a3447" }}
              >
                <Target size={16} /> Set Goals
              </button>
            )}
          </div>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        {kpiKeys.map((k) => (
          <KpiCard
            key={k}
            metricKey={k}
            current={data.current[k] || 0}
            monthlyGoal={data.monthly_goals[k] || 0}
            yearTotal={data.yearly_totals[k] || 0}
            annualGoal={data.annual_goals[k] || 0}
          />
        ))}
      </div>

      {/* Monthly performance chart */}
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold">Monthly Performance · {data.year}</h2>
            <p className="text-xs text-slate-500 mt-1">
              {view === "owner" ? "Server-wide joins, leaves, and engagement" : "Your tickets & server bumps by month"}
            </p>
          </div>
        </div>
        <div className="h-72 min-h-[288px]">
          <ResponsiveContainer width="100%" height={288}>
            {view === "owner" ? (
              <AreaChart data={historyForChart}>
                <defs>
                  <linearGradient id="areaJoins" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="var(--accent)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="areaLeaves" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff3b5c" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#ff3b5c" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#242c3d" strokeDasharray="3 3" />
                <XAxis dataKey="monthLabel" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={{ background: "#121620", border: "1px solid #2a3447", borderRadius: 10 }} />
                <Legend />
                <Area type="monotone" dataKey="joins" stroke="var(--accent)" fill="url(#areaJoins)" strokeWidth={2} name="Joins" />
                <Area type="monotone" dataKey="leaves" stroke="#ff3b5c" fill="url(#areaLeaves)" strokeWidth={2} name="Leaves" />
              </AreaChart>
            ) : (
              <BarChart data={historyForChart}>
                <CartesianGrid stroke="#242c3d" strokeDasharray="3 3" />
                <XAxis dataKey="monthLabel" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={{ background: "#121620", border: "1px solid #2a3447", borderRadius: 10 }} />
                <Legend />
                {data.ticket_keys.map((k, i) => (
                  <Bar key={k} dataKey={k} stackId="a" fill={METRIC_META[k].color} name={METRIC_META[k].label} />
                ))}
                <Bar dataKey="server_bumps" fill="#ff9f1c" name="Server Bumps" />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* Yearly attainment */}
      <div className="card-surface p-6">
        <div className="mb-4">
          <h2 className="text-lg font-bold">Yearly Attainment · {data.year}</h2>
          <p className="text-xs text-slate-500 mt-1">Year-to-date totals vs annual goals</p>
        </div>
        <div className="space-y-3">
          {kpiKeys.map((k) => {
            const total = data.yearly_totals[k] || 0;
            const goal = data.annual_goals[k] || 0;
            const p = pct(total, goal);
            const meta = METRIC_META[k];
            return (
              <div key={k} data-testid={`yearly-${k}`}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                    {meta.label}
                  </span>
                  <span className="text-slate-400 font-mono text-xs">
                    {Math.round(total).toLocaleString()} / {Math.round(goal).toLocaleString()}
                    <span className="ml-2 font-bold" style={{ color: p >= 100 ? "var(--accent)" : meta.color }}>{p}%</span>
                  </span>
                </div>
                <div className="h-2 bg-[#0b0d12] rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{ width: `${Math.min(100, p)}%`, background: meta.color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Monthly line chart per metric (owner) */}
      {view === "owner" && (
        <div className="card-surface p-6">
          <h2 className="text-lg font-bold mb-4">Engagement · Messages, Voice & Bumps</h2>
          <div className="h-64 min-h-[256px]">
            <ResponsiveContainer width="100%" height={256}>
              <LineChart data={historyForChart}>
                <CartesianGrid stroke="#242c3d" strokeDasharray="3 3" />
                <XAxis dataKey="monthLabel" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={{ background: "#121620", border: "1px solid #2a3447", borderRadius: 10 }} />
                <Legend />
                <Line type="monotone" dataKey="messages" stroke="#00e5ff" strokeWidth={2} dot={{ r: 3 }} name="Messages" />
                <Line type="monotone" dataKey="voice_hours" stroke="#ffc700" strokeWidth={2} dot={{ r: 3 }} name="Voice Hours" />
                <Line type="monotone" dataKey="server_bumps" stroke="#ff9f1c" strokeWidth={2} dot={{ r: 3 }} name="Server Bumps" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {showEntry && (
        <EntryModal
          view={view}
          currentMonth={data.current_month}
          onClose={() => setShowEntry(false)}
          onSaved={() => { load(); setShowEntry(false); toast.success("Metric saved"); }}
        />
      )}
      {showGoals && (
        <GoalsModal
          onClose={() => setShowGoals(false)}
          onSaved={() => { load(); toast.success("Goal saved"); }}
        />
      )}
    </div>
  );
}

function KpiCard({ metricKey, current, monthlyGoal, yearTotal, annualGoal }) {
  const meta = METRIC_META[metricKey];
  const Icon = meta.icon;
  const p = pct(current, monthlyGoal);
  return (
    <div className="card-surface p-5 relative overflow-hidden" data-testid={`kpi-${metricKey}`}>
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: meta.color }} />
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${meta.color}22`, color: meta.color }}>
          <Icon size={18} />
        </div>
        <div className="text-xs uppercase tracking-widest text-slate-400">{meta.label}</div>
      </div>
      <div className="mt-3 text-3xl font-extrabold tracking-tight">{Math.round(current).toLocaleString()}</div>
      <div className="text-xs text-slate-500 mt-1">
        this month · goal <span className="text-slate-300 font-semibold">{Math.round(monthlyGoal).toLocaleString()}</span>
      </div>
      <div className="mt-3 h-1.5 bg-[#0b0d12] rounded-full overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${Math.min(100, p)}%`, background: meta.color }} />
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span className="font-bold" style={{ color: p >= 100 ? "var(--accent)" : meta.color }}>{p}% to goal</span>
        <span className="text-slate-500">
          YTD {Math.round(yearTotal).toLocaleString()} / {Math.round(annualGoal).toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function EntryModal({ view, currentMonth, onClose, onSaved }) {
  const [month, setMonth] = useState(currentMonth);
  const [scope, setScope] = useState(view === "owner" ? "server" : "user");
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [values, setValues] = useState({});

  useEffect(() => {
    if (scope === "user") api.get("/users").then(({ data }) => { setUsers(data); if (data[0]) setUserId(data[0].id); });
  }, [scope]);

  const fields = scope === "server"
    ? [["joins", "Joins"], ["leaves", "Leaves"], ["messages", "Messages"], ["voice_hours", "Voice Hours"], ["server_bumps", "Server Bumps"]]
    : [["admin_tickets", "Admin Tickets"], ["age_verification_tickets", "Age Verification"], ["moderation_tickets", "Moderation Tickets"]];

  const submit = async (e) => {
    e.preventDefault();
    const payload = { month, scope, user_id: scope === "user" ? userId : null };
    for (const [k] of fields) {
      if (values[k] !== undefined && values[k] !== "") payload[k] = Number(values[k]);
    }
    await api.post("/metrics/entry", payload);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="entry-modal">
        <h2 className="text-xl font-bold mb-4">Log Monthly Metric</h2>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Month</label>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="entry-month" className="mt-2 w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Scope</label>
            <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="entry-scope" className="mt-2 w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]">
              <option value="server">Server-wide</option>
              <option value="user">Per Staff Member</option>
            </select>
          </div>
        </div>
        {scope === "user" && (
          <div className="mb-3">
            <label className="text-xs uppercase tracking-widest text-slate-500">Staff member</label>
            <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="entry-user" className="mt-2 w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]">
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
            </select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          {fields.map(([key, label]) => (
            <div key={key}>
              <label className="text-xs uppercase tracking-widest text-slate-500">{label}</label>
              <input
                type="number"
                min="0"
                step={key === "voice_hours" ? "0.1" : "1"}
                value={values[key] || ""}
                onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                data-testid={`entry-${key}`}
                className="mt-2 w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]"
              />
            </div>
          ))}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
          <button type="submit" data-testid="entry-submit" className="px-4 py-2 rounded-lg text-black font-bold text-sm" style={{ background: "var(--accent)" }}>Save</button>
        </div>
      </form>
    </div>
  );
}

function GoalsModal({ onClose, onSaved }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [goals, setGoals] = useState([]);
  const [busyKey, setBusyKey] = useState("");

  const load = () => api.get(`/metrics/goals?year=${year}`).then(({ data }) => setGoals(data));
  useEffect(() => { load(); }, [year]);

  const rows = useMemo(() => {
    const server = ["joins", "leaves", "messages", "voice_hours", "server_bumps"];
    const userMetrics = ["admin_tickets", "age_verification_tickets", "moderation_tickets"];
    const findGoal = (k, s) => goals.find((g) => g.metric_key === k && g.scope === s && (g.user_id == null)) || { monthly_goal: 0, annual_goal: 0 };
    return [
      ...server.map((k) => ({ key: k, scope: "server", label: METRIC_META[k].label, ...findGoal(k, "server") })),
      ...userMetrics.map((k) => ({ key: k, scope: "user", label: METRIC_META[k].label, ...findGoal(k, "user") })),
    ];
  }, [goals]);

  const save = async (row, monthly, annual) => {
    setBusyKey(row.key + row.scope);
    try {
      await api.post("/metrics/goals", {
        metric_key: row.key, year, scope: row.scope, user_id: null,
        monthly_goal: Number(monthly), annual_goal: Number(annual),
      });
      await load();
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="card-surface p-6 w-full max-w-2xl max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="goals-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2"><Target size={18} /> Goals · {year}</h2>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="goals-year" className="w-28 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm" />
        </div>
        <table className="w-full text-sm">
          <thead className="text-slate-400 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left py-2">Metric</th>
              <th className="text-left py-2">Scope</th>
              <th className="text-left py-2">Monthly</th>
              <th className="text-left py-2">Annual</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <GoalRow key={row.key + row.scope} row={row} busy={busyKey === row.key + row.scope} onSave={save} />
            ))}
          </tbody>
        </table>
        <div className="mt-5 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-black font-bold text-sm" style={{ background: "var(--accent)" }}>Done</button>
        </div>
      </div>
    </div>
  );
}

function GoalRow({ row, busy, onSave }) {
  const [monthly, setMonthly] = useState(row.monthly_goal || 0);
  const [annual, setAnnual] = useState(row.annual_goal || 0);
  useEffect(() => { setMonthly(row.monthly_goal || 0); setAnnual(row.annual_goal || 0); }, [row.monthly_goal, row.annual_goal]);
  return (
    <tr className="border-t border-[#1a202c]" data-testid={`goal-row-${row.key}-${row.scope}`}>
      <td className="py-3 font-semibold">{row.label}</td>
      <td className="py-3 text-slate-400 uppercase text-xs tracking-widest">{row.scope}</td>
      <td className="py-3"><input type="number" value={monthly} onChange={(e) => setMonthly(e.target.value)} data-testid={`goal-monthly-${row.key}-${row.scope}`} className="w-28 bg-[#0b0d12] border border-[#2a3447] rounded px-2 py-1 outline-none focus:border-[color:var(--accent)]" /></td>
      <td className="py-3"><input type="number" value={annual} onChange={(e) => setAnnual(e.target.value)} data-testid={`goal-annual-${row.key}-${row.scope}`} className="w-32 bg-[#0b0d12] border border-[#2a3447] rounded px-2 py-1 outline-none focus:border-[color:var(--accent)]" /></td>
      <td className="py-3 text-right">
        <button
          onClick={() => onSave(row, monthly, annual)}
          disabled={busy}
          data-testid={`goal-save-${row.key}-${row.scope}`}
          className="px-3 py-1.5 rounded text-xs text-black font-bold disabled:opacity-60"
          style={{ background: "var(--accent)" }}
        >
          {busy ? "…" : "Save"}
        </button>
      </td>
    </tr>
  );
}
